'use client'

import { Inter } from 'next/font/google'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import { usePathname } from 'next/navigation'
import './globals.css'
import React from 'react'

import Header from '@/components/header'
import Body from '@/components/body'
import Analytics from '@/components/analytics'
import Footer from '@/components/footer-v2'
import { Toaster } from '@/components/ui/toaster'
import { LanguageProvider } from '@/i18n/provider'
import ThemeProvider from '@/components/theme-provider'
import { ORD_ROOT_URL } from '@/utils/constants'

const Controllers = dynamic(() => import('@/components/controllers'))

const inter = Inter({ subsets: ['latin'] })

export default function RootLayout(props: { children: React.ReactNode }) {
	// /alkanes owns the whole viewport: no site header, footer, or page-width
	// wrapper. Decided from the pathname during render — the SSR pass and the
	// first client render agree, so the chrome never flashes in and out.
	const pathname = usePathname()
	const fullscreen = pathname?.startsWith('/alkanes') ?? false
	return (
		<html lang="en" suppressHydrationWarning>
			<link rel="manifest" href="/manifest.json" />
			<link rel="icon" type="image/png" sizes="512x512" href="/icon-512x512.png" />
			<link rel="icon" type="image/png" sizes="384x384" href="/icon-384x384.png" />
			<link rel="icon" type="image/png" sizes="256x256" href="/icon-256x256.png" />
			<link rel="icon" type="image/png" sizes="192x192" href="/icon-192x192.png" />
			<meta name="apple-mobile-web-app-capable" content="yes" />
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2048-2732.jpg"
				media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2732-2048.jpg"
				media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1668-2388.jpg"
				media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2388-1668.jpg"
				media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1536-2048.jpg"
				media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2048-1536.jpg"
				media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1668-2224.jpg"
				media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2224-1668.jpg"
				media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1620-2160.jpg"
				media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2160-1620.jpg"
				media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1290-2796.jpg"
				media="(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2796-1290.jpg"
				media="(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1179-2556.jpg"
				media="(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2556-1179.jpg"
				media="(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1284-2778.jpg"
				media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2778-1284.jpg"
				media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1170-2532.jpg"
				media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2532-1170.jpg"
				media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1125-2436.jpg"
				media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2436-1125.jpg"
				media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1242-2688.jpg"
				media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2688-1242.jpg"
				media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-828-1792.jpg"
				media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1792-828.jpg"
				media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1242-2208.jpg"
				media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-2208-1242.jpg"
				media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-750-1334.jpg"
				media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1334-750.jpg"
				media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-640-1136.jpg"
				media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
			/>
			<link
				rel="apple-touch-startup-image"
				href="/apple-splash-1136-640.jpg"
				media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
			/>

			{/*
				<head /> will contain the components returned by the nearest parent
				head.tsx. Find out more at https://beta.nextjs.org/docs/api-reference/file-conventions/head
			*/}
			<head>
				<meta
					name="google-site-verification"
					content="KIzmsMVUoJkg3hRU7XBF3_5Gx8T8JCowbvm1cxe_GQY"
				/>
				{process.env.NEXT_PUBLIC_PERF === '1' && (
					<script
						// Pretends to be React DevTools so React notifies us on every commit.
						// Used by Playwright tests to count React commits per interaction.
						// Must run BEFORE react-dom imports so the hook is in place when React boots.
						dangerouslySetInnerHTML={{
							__html: `(function(){window.__renderCount=0;window.__renderEvents=[];window.__renderReset=function(){window.__renderCount=0;window.__renderEvents=[];};var h=window.__REACT_DEVTOOLS_GLOBAL_HOOK__=window.__REACT_DEVTOOLS_GLOBAL_HOOK__||{checkDCE:function(){},inject:function(){return 1;},onScheduleFiberRoot:function(){},onCommitFiberRoot:function(){},onCommitFiberUnmount:function(){},supportsFiber:true,renderers:new Map(),listeners:{},sub:function(){return function(){};}};var o=h.onCommitFiberRoot;h.onCommitFiberRoot=function(){window.__renderCount+=1;if(window.__renderEvents.length<1000){window.__renderEvents.push(performance.now());}if(o)try{o.apply(this,arguments);}catch(e){}};})();`
						}}
					/>
				)}
				{ORD_ROOT_URL && (
					<>
						<link rel="dns-prefetch" href={ORD_ROOT_URL} />
						<link rel="preconnect" href={ORD_ROOT_URL} crossOrigin="anonymous" />
					</>
				)}
				{process.env.NEXT_PUBLIC_CDN_URL && (
					<>
						<link rel="dns-prefetch" href={process.env.NEXT_PUBLIC_CDN_URL} />
						<link rel="preconnect" href={process.env.NEXT_PUBLIC_CDN_URL} crossOrigin="anonymous" />
					</>
				)}
				{process.env.NODE_ENV === 'development' && (
					<>
						<Script
							src="//unpkg.com/react-grab/dist/index.global.js"
							strategy="beforeInteractive"
						/>
						<Script
							src="//unpkg.com/@react-grab/cursor/dist/client.global.js"
							strategy="lazyOnload"
						/>
					</>
				)}
			</head>
			<body className={inter.className}>
				<ThemeProvider>
					<LanguageProvider>
						{!fullscreen && <Header />}
						{fullscreen ? props.children : <Body>{props.children}</Body>}
						{!fullscreen && <Footer />}
						<Controllers />
						<Toaster />
					</LanguageProvider>
				</ThemeProvider>
			</body>
			<Analytics />
		</html>
	)
}

export const fetchCache = 'force-cache'
export const maxDuration = 5
